import { FC, useReducer } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Button, FormHelperText } from '@mui/material';
import { useErrorHandler, ErrorFieldType } from 'src/app/common/utils';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { NOTE_PATH, SIMULATION_TOOL_PATH } from 'src/app/modules/SimulationTool/constants';
import { NoteItem } from 'src/app/modules/SimulationTool/types/note-setting';
import { UpdateNoteBody, updateNoteItem } from 'src/app/modules/SimulationTool/network/noteCrud';
import Tinymce from 'src/app/modules/News/pages/Article/View/components/Tinymce';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';

type NoteFormProps = {
  noteItem?: NoteItem;
};

type NoteFormState = {
  title?: string;
  content?: string;
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof NoteFormState;
    value: any;
  };
};

type NoteFormAction = ModifyFieldAction;

const formReducer = (state: NoteFormState, action: NoteFormAction): NoteFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    default:
      return state;
  }
};

const detailToStateConvertor = (noteItem?: NoteItem): NoteFormState => {
  if (noteItem) {
    return {
      title: noteItem.title,
      content: noteItem.content,
    };
  } else {
    return {
      title: '',
      content: '',
    };
  }
};

const FIELD_CONTAINER_WIDTH = 160;
const FIELD_WIDTH = 200;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rowSpacing: {
    marginBottom: 16,
  },
  fieldContainer: {
    width: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
  },
  fieldBox: {
    minWidth: FIELD_WIDTH,
  },
  noMargin: {
    margin: 0,
  },
  mandatory: {
    color: 'red',
  },
  sectionMargin: {
    marginBottom: 15,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

const NoteSettingForm: FC<NoteFormProps> = ({ noteItem }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);
  const dispatch = useDispatch();
  const history = useHistory();

  const [formState, formDispatch] = useReducer(formReducer, detailToStateConvertor(noteItem));

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler } = useErrorHandler(formState, [
    {
      name: 'title',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'content',
      fieldType: ErrorFieldType.MANDATORY,
    },
  ]);

  const onSubmit = async () => {
    const { hasError } = onSubmitErrorValidator();
    if (!hasError) {
      const body: UpdateNoteBody = {
        title: formState.title || '',
        content: formState.content || '',
      };
      try {
        if (noteItem) {
          await updateNoteItem(noteItem._id, body, dispatch);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: `Note updated successfully`,
              },
            ]),
          );
        }
        history.push(`${SIMULATION_TOOL_PATH}${NOTE_PATH}`);
      } catch (err) {}
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>{Translation(`noteSetting.form.title.edit`)}</div>
        </div>
        <Button variant="contained" color="inherit" onClick={() => history.push(`${SIMULATION_TOOL_PATH}${NOTE_PATH}`)}>
          {Translation('app.button.back')}
        </Button>
      </div>
      <div className={classes.sectionMargin}>
        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('noteSetting.title')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Tinymce
              disabled={false}
              value={formState.title}
              accessLevel={'anonymous'}
              onChange={(value: string) =>
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'title', value: value } })
              }
            />
            {errorState.mandatory.title && (
              <FormHelperText style={{ color: '#f018a6' }}>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
            )}
          </div>
        </div>

        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('noteSetting.content')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Tinymce
              disabled={false}
              value={formState.content}
              accessLevel={'anonymous'}
              onChange={(value: string) =>
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'content', value: value } })
              }
            />
            {errorState.mandatory.content && (
              <FormHelperText style={{ color: '#f018a6' }}>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
            )}
          </div>
        </div>
      </div>
      <div className={classes.footerContainer}>
        <Button variant="contained" color="secondary" onClick={onSubmit}>
          {Translation('app.button.submit')}
        </Button>
      </div>
    </div>
  );
};

export default NoteSettingForm;
